import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Fields.css";
import { UserContext } from "../../UserContext";
import axios from "axios";
import toast from "react-hot-toast"; // Add this line

// Mock function to simulate fetching data
// const fetchCropCycleById = async (id) => {
//   console.log(`Fetching data for crop cycle ${id}...`);
//   // In a real app, you would make an API call: GET /api/plantation/{id}
//   return {
//     cropId: 102, // Mocked: Mango
//     plantationDate: "2025-08-21",
//     expectedHarvestDate: "2025-08-22",
//     actualHarvestDate: "2025-08-28",
//   };
// };

// // Mock function to simulate fetching available crops
// const fetchAvailableCrops = async () => {
//   console.log("Fetching available crops...");
//   // In a real app, you would make an API call: GET /api/crop
//   return [
//     { id: 101, name: "عباد الشمس" },
//     { id: 102, name: "مانجا" },
//     { id: 103, name: "قمح" },
//   ];
// };

// Converts API timestamp (milliseconds) to "YYYY-MM-DD" for form inputs
const formatDateForInput = (timestamp) => {
  if (!timestamp) return "";
  return new Date(timestamp).toISOString().split("T")[0];
};

// Converts "YYYY-MM-DD" from form input back to a timestamp for the API
const convertDateToTimestamp = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).getTime();
};

const CropCycleForm = () => {
  const { t, i18n } = useTranslation();
  const { fieldId, plantationId } = useParams();
  const navigate = useNavigate();
  const { userData, token } = useContext(UserContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!plantationId; // Check if we are in "edit" mode

  const [formData, setFormData] = useState({
    cropId: "",
    cropCategoryId: "",
    plantationDate: "",
    expectedHarvestDate: "",
    actualHarvestDate: "",
  });
  const [availableCrops, setAvailableCrops] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!token) {
        setLoading(false);
        return; // Can't fetch without a token
      }
      setLoading(true);

      try {
        // Fetch the list of available crops from GET /api/crop
        const cropsResponse = await axios.get("api/crop", {
          headers: { Authorization: token },
        });
        setAvailableCrops(cropsResponse.data.items || []);

        // If editing, fetch existing data from GET /api/plantation/{id}
        if (isEditing) {
          const existingDataResponse = await axios.get(
            `api/plantation/${plantationId}`,
            { headers: { Authorization: token } }
          );
          const existingData = existingDataResponse.data.items[0];

          // Format dates from API timestamps to input-compatible strings
          setFormData({
            cropId: existingData.cropId,
            cropCategoryId: existingData.cropCategoryId || "",
            plantationDate: formatDateForInput(existingData.plantationDate),
            expectedHarvestDate: formatDateForInput(
              existingData.expectedHarvestDate
            ),
            actualHarvestDate: formatDateForInput(
              existingData.actualHarvestDate
            ),
          });

          // If editing and has a cropId, fetch categories for that crop
          if (existingData.cropId && userData?.Tenant?.id) {
            try {
              const categoriesResponse = await axios.get(
                `api/cropCategories/findByTenantIdAndCropId/${userData.Tenant.id}/${existingData.cropId}`,
                { headers: { Authorization: token } }
              );
              setAvailableCategories(categoriesResponse.data.items || []);
            } catch (error) {
              console.error("Failed to load crop categories", error);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load data for crop cycle form", error);
        toast.error(t("Failed to load necessary data. Please try again."));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isEditing, plantationId, token, t]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // If crop changes, fetch its categories
    if (name === "cropId" && value && userData?.Tenant?.id) {
      try {
        const categoriesResponse = await axios.get(
          `api/cropCategories/findByTenantIdAndCropId/${userData.Tenant.id}/${value}`,
          { headers: { Authorization: token } }
        );
        setAvailableCategories(categoriesResponse.data.items || []);
        // Reset category selection when crop changes
        setFormData((prev) => ({ ...prev, cropCategoryId: "" }));
      } catch (error) {
        console.error("Failed to load crop categories", error);
        setAvailableCategories([]);
      }
    }
  };

  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   if (isEditing) {
  //     // Logic for updating an existing crop cycle
  //     // API Call: PUT /api/plantation with formData
  //     alert(`Updating Crop Cycle ${plantationId} for Field ${fieldId}`);
  //   } else {
  //     // Logic for adding a new crop cycle
  //     // API Call: POST /api/plantation with formData
  //     alert(`Adding New Crop Cycle to Field ${fieldId}`);
  //   }
  //   navigate(`/fields/${fieldId}`); // Navigate back to the field details page
  // };

  // Updated handleSubmit for API submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const farmId = userData?.Farm?.id;
    const tenantId = userData?.Tenant?.id;

    if (!farmId || !tenantId) {
      toast.error("Error: Farm and Tenant information is missing.");
      setIsSubmitting(false);
      return;
    }

    const payload = {
      cropId: parseInt(formData.cropId),
      plantationDate: convertDateToTimestamp(formData.plantationDate),
      expectedHarvestDate: convertDateToTimestamp(formData.expectedHarvestDate),
      actualHarvestDate: convertDateToTimestamp(formData.actualHarvestDate),
      fieldId: parseInt(fieldId),
      farmId: farmId,
      tenantId: tenantId,
    };

    // Only include cropCategoryId if a category was selected
    if (formData.cropCategoryId) {
      payload.cropCategoryId = parseInt(formData.cropCategoryId);
    }

    try {
      if (isEditing) {
        payload.id = parseInt(plantationId);
        await axios.put("api/plantation", payload, {
          headers: { Authorization: token },
        });
        toast.success(t("Crop Cycle updated successfully!"));
      } else {
        await axios.post("api/plantation", payload, {
          headers: { Authorization: token },
        });
        toast.success(t("Crop Cycle added successfully!"));
      }
      navigate(`/fields/${fieldId}`);
    } catch (error) {
      console.error("Failed to save crop cycle:", error);
      toast.error(t("An error occurred."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div>{t("Loading...")}</div>;
  }

  return (
    <div className="form-container">
      <div className="form-card">
        <header className="view-header">
          <h1 className="view-header-title-group">
            {isEditing ? t("Edit Crop Cycle") : t("Add Crop Cycle")}
          </h1>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="cropId">{t("Crop")}</label>
            <select
              id="cropId"
              name="cropId"
              value={formData.cropId}
              onChange={handleChange}
              required
            >
              <option value="" disabled>
                {t("Select a crop")}
              </option>
              {/* {availableCrops.map((crop) => (
                <option key={crop.id} value={crop.id}>
                  {crop.name}
                </option>
              ))} */}
              {/* options for api crop types */}
              {availableCrops.map((crop) => (
                <option key={crop.id} value={crop.id}>
                  {i18n.language === "ar" ? crop.nameAr : crop.nameEn}
                </option>
              ))}
            </select>
          </div>

          {availableCategories.length > 0 && (
            <div className="form-group">
              <label htmlFor="cropCategoryId">{t("Crop Category")}</label>
              <select
                id="cropCategoryId"
                name="cropCategoryId"
                value={formData.cropCategoryId}
                onChange={handleChange}
              >
                <option value="">
                  {t("Select a category (optional)")}
                </option>
                {availableCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.category}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="plantationDate">{t("Planting Date")}</label>
            <input
              type="date"
              id="plantationDate"
              name="plantationDate"
              value={formData.plantationDate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="expectedHarvestDate">
              {t("Expected Harvest Date")}
            </label>
            <input
              type="date"
              id="expectedHarvestDate"
              name="expectedHarvestDate"
              value={formData.expectedHarvestDate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="actualHarvestDate">
              {t("Actual Harvest Date")}
            </label>
            <input
              type="date"
              id="actualHarvestDate"
              name="actualHarvestDate"
              value={formData.actualHarvestDate}
              onChange={handleChange}
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="button-secondary"
              onClick={() => navigate(`/fields/${fieldId}`)}
              disabled={isSubmitting}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t("Saving...")
                : isEditing
                ? t("Update")
                : t("Add")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CropCycleForm;
